import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Feature analytics.
 *
 * Port of `src/app/admin/features/analytics/feature-analytics-client.tsx`
 * (legacy NextJS, ~345 LOC) per D21.
 *
 * Endpoint: GET /api/admin/features/analytics?since=&scope=
 *   Returns { rows: Row[], kpis: Kpis }
 */

type FeatureScope = "user" | "provider" | "facility" | "business";

interface Row {
  featureKey: string;
  label: string;
  category: string;
  scope: FeatureScope | null;
  impressions: number;
  upgradeClicks: number;
  waitlistClicks: number;
  dismisses: number;
  conversionRate: number;
  checkouts: number;
  checkoutRate: number;
  lastEventAt: string | null;
}

interface Kpis {
  totalImpressions: number;
  totalUpgradeClicks: number;
  totalWaitlistClicks: number;
  totalCheckouts: number;
  avgConversion: number;
  avgCheckoutRate: number;
  featuresWithActivity: number;
}

type SortKey = "impressions" | "conversion" | "checkouts" | "recent";
type ScopeFilter = "all" | FeatureScope;
type SinceFilter = 7 | 30 | 90;

export const Route = createFileRoute("/_authenticated/features-analytics")({
  component: FeatureAnalyticsPage,
});

function FeatureAnalyticsPage() {
  const { t } = useLingui();
  const { dbTarget } = useAdminContext();
  const [rows, setRows] = useState<Row[]>([]);
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<ScopeFilter>("all");
  const [since, setSince] = useState<SinceFilter>(30);
  const [sortBy, setSortBy] = useState<SortKey>("impressions");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ since: String(since) });
    if (scope !== "all") params.set("scope", scope);

    apiFetch
      .get<{ rows: Row[]; kpis: Kpis | null }>(`/api/admin/features/analytics?${params.toString()}`)
      .then((data) => {
        if (cancelled) return;
        setRows(data.rows ?? []);
        setKpis(data.kpis ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setKpis(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scope, since, dbTarget]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    if (sortBy === "impressions") copy.sort((a, b) => b.impressions - a.impressions);
    else if (sortBy === "conversion") copy.sort((a, b) => b.conversionRate - a.conversionRate);
    else if (sortBy === "checkouts")
      copy.sort((a, b) => b.checkouts - a.checkouts || b.checkoutRate - a.checkoutRate);
    else
      copy.sort((a, b) => {
        const ta = a.lastEventAt ? Date.parse(a.lastEventAt) : 0;
        const tb = b.lastEventAt ? Date.parse(b.lastEventAt) : 0;
        return tb - ta;
      });
    return copy;
  }, [rows, sortBy]);

  const fmt = (n: number) => n.toLocaleString("en-US");
  const pct = (n: number) => `${n.toFixed(1)}%`;

  const scopeLabel: Record<ScopeFilter, React.ReactNode> = {
    all: <Trans>All</Trans>,
    user: <Trans>Users</Trans>,
    provider: <Trans>Providers</Trans>,
    facility: <Trans>Facilities</Trans>,
    business: <Trans>Businesses</Trans>,
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Feature analytics</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Impressions, upgrade clicks, conversion rates per feature</Trans>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground text-xs">
            <Trans>Scope:</Trans>
          </span>
          {(["all", "user", "provider", "facility", "business"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-medium transition-all ${
                scope === s
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {scopeLabel[s]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <span className="text-muted-foreground text-xs">
            <Trans>Range:</Trans>
          </span>
          {([7, 30, 90] as const).map((d) => (
            <button
              key={d}
              onClick={() => setSince(d)}
              className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-medium transition-all ${
                since === d
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <span className="text-muted-foreground text-xs">
            <Trans>Sort:</Trans>
          </span>
          {(["impressions", "conversion", "checkouts", "recent"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setSortBy(s)}
              className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-medium transition-all ${
                sortBy === s
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label={t`Impressions`}
          value={kpis ? fmt(kpis.totalImpressions) : "—"}
          loading={loading}
        />
        <KpiCard
          label={t`Upgrade clicks`}
          value={kpis ? fmt(kpis.totalUpgradeClicks) : "—"}
          loading={loading}
        />
        <KpiCard
          label={t`Waitlist clicks`}
          value={kpis ? fmt(kpis.totalWaitlistClicks) : "—"}
          loading={loading}
        />
        <KpiCard
          label={t`Avg conversion`}
          value={kpis ? pct(kpis.avgConversion) : "—"}
          loading={loading}
        />
        <KpiCard
          label={t`Checkouts`}
          value={kpis ? fmt(kpis.totalCheckouts) : "—"}
          loading={loading}
        />
        <KpiCard
          label={t`Checkout rate`}
          value={kpis ? pct(kpis.avgCheckoutRate) : "—"}
          loading={loading}
        />
      </div>

      {loading ? (
        <Card>
          <CardContent className="text-muted-foreground p-8 text-center text-sm">
            <Trans>Loading...</Trans>
          </CardContent>
        </Card>
      ) : sortedRows.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground p-8 text-center text-sm">
            <Trans>No data for this range</Trans>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-border/50 bg-muted/30 sticky top-0 border-b">
                  <th className="min-w-[280px] px-3 py-2 text-left font-semibold">
                    <Trans>Feature</Trans>
                  </th>
                  <th className="hidden px-3 py-2 text-left font-semibold md:table-cell">
                    <Trans>Category</Trans>
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    <Trans>Impressions</Trans>
                  </th>
                  <th className="hidden px-3 py-2 text-right font-semibold sm:table-cell">
                    <Trans>Upgrades</Trans>
                  </th>
                  <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
                    <Trans>Waitlist</Trans>
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    <Trans>Conversion</Trans>
                  </th>
                  <th className="hidden px-3 py-2 text-right font-semibold sm:table-cell">
                    <Trans>Checkouts</Trans>
                  </th>
                  <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
                    <Trans>Checkout rate</Trans>
                  </th>
                  <th className="hidden px-3 py-2 text-right font-semibold lg:table-cell">
                    <Trans>Last event</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr key={r.featureKey} className="border-border/30 hover:bg-muted/20 border-b">
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.label}</div>
                      <div className="text-muted-foreground mt-0.5 font-mono text-[0.5625rem]">
                        {r.featureKey}
                      </div>
                    </td>
                    <td className="hidden px-3 py-2 md:table-cell">
                      <Badge variant="outline" className="h-4 text-[0.5625rem]">
                        {r.category}
                      </Badge>
                      {r.scope && (
                        <Badge variant="outline" className="ml-1 h-4 text-[0.5625rem]">
                          {r.scope}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.impressions)}</td>
                    <td className="hidden px-3 py-2 text-right font-mono sm:table-cell">
                      {fmt(r.upgradeClicks)}
                    </td>
                    <td className="hidden px-3 py-2 text-right font-mono md:table-cell">
                      {fmt(r.waitlistClicks)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      <span
                        className={
                          r.conversionRate >= 5
                            ? "text-emerald-600 dark:text-emerald-400"
                            : r.conversionRate >= 1
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-muted-foreground"
                        }
                      >
                        {pct(r.conversionRate)}
                      </span>
                    </td>
                    <td className="hidden px-3 py-2 text-right font-mono sm:table-cell">
                      <span
                        className={
                          r.checkouts > 0
                            ? "font-semibold text-emerald-600 dark:text-emerald-400"
                            : "text-muted-foreground"
                        }
                      >
                        {fmt(r.checkouts)}
                      </span>
                    </td>
                    <td className="hidden px-3 py-2 text-right font-mono md:table-cell">
                      <span
                        className={
                          r.checkoutRate >= 10
                            ? "text-emerald-600 dark:text-emerald-400"
                            : r.checkoutRate >= 2
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-muted-foreground"
                        }
                      >
                        {r.upgradeClicks > 0 ? pct(r.checkoutRate) : "—"}
                      </span>
                    </td>
                    <td className="text-muted-foreground hidden px-3 py-2 text-right font-mono lg:table-cell">
                      {r.lastEventAt ? new Date(r.lastEventAt).toLocaleDateString("en-GB") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KpiCard({ label, value, loading }: { label: string; value: string; loading: boolean }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-muted-foreground text-[0.6875rem] font-semibold tracking-wider uppercase">
          {label}
        </div>
        <div className="mt-1 text-2xl font-bold">
          {loading ? (
            <span className="bg-muted inline-block h-7 w-16 animate-pulse rounded" />
          ) : (
            value
          )}
        </div>
      </CardContent>
    </Card>
  );
}
